import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, HelpCircle, Lightbulb, Info, AlertTriangle, CheckCircle } from 'lucide-react';

export type TooltipType = 'info' | 'help' | 'hint' | 'warning' | 'success' | 'feature';

export interface SmartTooltipProps {
  content: string | React.ReactNode;
  type?: TooltipType;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  trigger?: 'hover' | 'click' | 'focus' | 'manual';
  delay?: number;
  duration?: number; // Auto-hide after this many seconds
  interactive?: boolean;
  showArrow?: boolean;
  className?: string;
  children: React.ReactNode;
  onShow?: () => void;
  onHide?: () => void;
  disabled?: boolean;
}

export const SmartTooltip: React.FC<SmartTooltipProps> = ({
  content,
  type = 'info',
  position = 'auto',
  trigger = 'hover',
  delay = 300,
  duration,
  interactive = false,
  showArrow = true,
  className = '',
  children,
  onShow,
  onHide,
  disabled = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const hideTimerRef = useRef<NodeJS.Timeout>();

  const getIcon = () => {
    switch (type) {
      case 'help':
        return <HelpCircle className="w-4 h-4" />;
      case 'hint':
        return <Lightbulb className="w-4 h-4" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />;
      case 'success':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'warning':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          text: 'text-orange-800 dark:text-orange-200',
          icon: 'text-orange-500'
        };
      case 'success':
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          text: 'text-green-800 dark:text-green-200',
          icon: 'text-green-500'
        };
      case 'help':
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          text: 'text-blue-800 dark:text-blue-200',
          icon: 'text-blue-500'
        };
      case 'hint':
        return {
          bg: 'bg-purple-50 dark:bg-purple-900/20',
          border: 'border-purple-200 dark:border-purple-800',
          text: 'text-purple-800 dark:text-purple-200',
          icon: 'text-purple-500'
        };
      default: // info
        return {
          bg: 'bg-gray-50 dark:bg-gray-900/20',
          border: 'border-gray-200 dark:border-gray-800',
          text: 'text-gray-800 dark:text-gray-200',
          icon: 'text-gray-500'
        };
    }
  };

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return 'top';

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const positions = {
      top: triggerRect.top - tooltipRect.height - 8,
      bottom: triggerRect.bottom + 8,
      left: triggerRect.left - tooltipRect.width - 8,
      right: triggerRect.right + 8
    };

    // Check if position fits in viewport
    const fits = {
      top: positions.top >= 0,
      bottom: positions.bottom + tooltipRect.height <= viewportHeight,
      left: positions.left >= 0,
      right: positions.right + tooltipRect.width <= viewportWidth
    };

    if (position === 'auto') {
      // Prefer top, then bottom, then sides
      if (fits.top) return 'top';
      if (fits.bottom) return 'bottom';
      if (fits.left) return 'left';
      if (fits.right) return 'right';
      return 'top'; // fallback
    }

    return fits[position] ? position : 'top';
  };

  const getTooltipStyle = () => {
    if (!triggerRef.current) return {};

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const pos = actualPosition;

    switch (pos) {
      case 'top':
        return {
          top: triggerRect.top - 8,
          left: triggerRect.left + (triggerRect.width / 2),
          transform: 'translateX(-50%) translateY(-100%)'
        };
      case 'bottom':
        return {
          top: triggerRect.bottom + 8,
          left: triggerRect.left + (triggerRect.width / 2),
          transform: 'translateX(-50%)'
        };
      case 'left':
        return {
          top: triggerRect.top + (triggerRect.height / 2),
          left: triggerRect.left - 8,
          transform: 'translateX(-100%) translateY(-50%)'
        };
      case 'right':
        return {
          top: triggerRect.top + (triggerRect.height / 2),
          left: triggerRect.right + 8,
          transform: 'translateY(-50%)'
        };
      default:
        return {};
    }
  };

  const getArrowStyle = () => {
    switch (actualPosition) {
      case 'top':
        return {
          bottom: '-4px',
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: '4px solid transparent',
          borderRight: '4px solid transparent',
          borderTop: `4px solid ${type === 'warning' ? '#fed7aa' : type === 'success' ? '#dcfce7' : type === 'help' ? '#dbeafe' : type === 'hint' ? '#faf5ff' : '#f9fafb'}`
        };
      case 'bottom':
        return {
          top: '-4px',
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: '4px solid transparent',
          borderRight: '4px solid transparent',
          borderBottom: `4px solid ${type === 'warning' ? '#fed7aa' : type === 'success' ? '#dcfce7' : type === 'help' ? '#dbeafe' : type === 'hint' ? '#faf5ff' : '#f9fafb'}`
        };
      case 'left':
        return {
          right: '-4px',
          top: '50%',
          transform: 'translateY(-50%)',
          borderTop: '4px solid transparent',
          borderBottom: '4px solid transparent',
          borderLeft: `4px solid ${type === 'warning' ? '#fed7aa' : type === 'success' ? '#dcfce7' : type === 'help' ? '#dbeafe' : type === 'hint' ? '#faf5ff' : '#f9fafb'}`
        };
      case 'right':
        return {
          left: '-4px',
          top: '50%',
          transform: 'translateY(-50%)',
          borderTop: '4px solid transparent',
          borderBottom: '4px solid transparent',
          borderRight: `4px solid ${type === 'warning' ? '#fed7aa' : type === 'success' ? '#dcfce7' : type === 'help' ? '#dbeafe' : type === 'hint' ? '#faf5ff' : '#f9fafb'}`
        };
      default:
        return {};
    }
  };

  const showTooltip = () => {
    if (disabled) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setIsVisible(true);
      setActualPosition(calculatePosition());
      onShow?.();

      // Auto-hide timer
      if (duration) {
        hideTimerRef.current = setTimeout(() => {
          hideTooltip();
        }, duration * 1000);
      }
    }, delay);
  };

  const hideTooltip = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }

    setIsVisible(false);
    onHide?.();
  };

  const handleTrigger = () => {
    if (trigger === 'click') {
      if (isVisible) {
        hideTooltip();
      } else {
        showTooltip();
      }
    }
  };

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      showTooltip();
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover' && !interactive) {
      hideTooltip();
    }
  };

  const handleFocus = () => {
    if (trigger === 'focus') {
      showTooltip();
    }
  };

  const handleBlur = () => {
    if (trigger === 'focus') {
      hideTooltip();
    }
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  const colors = getColors();

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        ref={triggerRef}
        onClick={handleTrigger}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTrigger(); } }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="inline-block"
        role="button"
        tabIndex={0}
      >
        {children}
      </div>

      <AnimatePresence>
        {isVisible && (
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 max-w-xs w-max ${colors.bg} border ${colors.border} rounded-lg shadow-lg pointer-events-auto`}
            style={getTooltipStyle()}
            onMouseEnter={() => interactive && trigger === 'hover' && showTooltip()}
            onMouseLeave={() => interactive && trigger === 'hover' && hideTooltip()}
          >
            {/* Arrow */}
            {showArrow && (
              <div
                className="absolute w-0 h-0"
                style={getArrowStyle()}
              />
            )}

            <div className="p-3">
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 mt-0.5 ${colors.icon}`}>
                  {getIcon()}
                </div>
                <div className={`flex-1 text-sm ${colors.text}`}>
                  {content}
                </div>
                {trigger === 'click' && (
                  <button
                    onClick={hideTooltip}
                    className="flex-shrink-0 p-1 hover:bg-black/10 rounded transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Context for managing multiple tooltips
export interface TooltipContextType {
  showTooltip: (id: string, content: string | React.ReactNode, options?: Partial<SmartTooltipProps>) => void;
  hideTooltip: (id: string) => void;
  hideAllTooltips: () => void;
}

export const TooltipContext = React.createContext<TooltipContextType | undefined>(undefined);

// Hook for programmatic tooltip control with visibility state
export const useTooltip = () => {
  const context = React.useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }

  const [isVisible, setIsVisible] = useState(false);

  const showTooltipWithState = (content: string | React.ReactNode, options?: Partial<SmartTooltipProps>) => {
    setIsVisible(true);
    context.showTooltip('programmatic-tooltip', content, options);
  };

  const hideTooltipWithState = () => {
    setIsVisible(false);
    context.hideTooltip('programmatic-tooltip');
  };

  return {
    ...context,
    isVisible,
    showTooltip: showTooltipWithState,
    hideTooltip: hideTooltipWithState
  };
};

export const useSmartTooltip = () => {
  const context = React.useContext(TooltipContext);
  if (!context) {
    throw new Error('useSmartTooltip must be used within a TooltipProvider');
  }
  return context;
};

// Provider component for managing tooltips globally
export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tooltips, setTooltips] = useState<Map<string, { content: string | React.ReactNode; options?: Partial<SmartTooltipProps> }>>(new Map());

  const showTooltip = (id: string, content: string | React.ReactNode, options?: Partial<SmartTooltipProps>) => {
    setTooltips(prev => new Map(prev.set(id, { content, options })));
  };

  const hideTooltip = (id: string) => {
    setTooltips(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  };

  const hideAllTooltips = () => {
    setTooltips(new Map());
  };

  const value: TooltipContextType = {
    showTooltip,
    hideTooltip,
    hideAllTooltips
  };

  return (
    <TooltipContext.Provider value={value}>
      {children}
      {/* Render global tooltips */}
      {Array.from(tooltips.entries()).map(([id, { content, options }]) => (
        <GlobalTooltip key={id} id={id} content={content} {...options} />
      ))}
    </TooltipContext.Provider>
  );
};

// Global tooltip component for programmatic tooltip display
const GlobalTooltip: React.FC<{ id: string; content: string | React.ReactNode; options?: Partial<SmartTooltipProps> }> = ({ id, content, options }) => {
  const [targetElement, setTargetElement] = useState<Element | null>(null);

  useEffect(() => {
    // Find element by data attribute
    const element = document.querySelector(`[data-tooltip-id="${id}"]`);
    setTargetElement(element);
  }, [id]);

  if (!targetElement) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: targetElement.getBoundingClientRect().top,
        left: targetElement.getBoundingClientRect().left,
        width: targetElement.getBoundingClientRect().width,
        height: targetElement.getBoundingClientRect().height,
        pointerEvents: 'none'
      }}
    >
      <SmartTooltip {...options} trigger="manual" content={content}>
        <div /> {/* Invisible trigger */}
      </SmartTooltip>
    </div>
  );
};